import dayjs, { type Dayjs } from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { calendarDayKey, parseProfileOffset } from '@/ui/calendarTime'
import { useAuthStore } from '@/stores/authStore'
import type { ArchiveResult, ExchangeType, Lifecycle, LegacyStatus, RequestLifecycle, ResponseSource, UserShort, UserVacancy, Vacancy } from './exchangeTypes'

dayjs.extend(utc)

// Два вида времени (target-contract «Уточнения после review RD-165», integration-requirements «Время»):
// - рабочее время вакансии/смены (dttm_work_*) — naive локальное время подразделения без Z/offset:
//   10:00 магазина передаётся и приходит как …T10:00:00; читается через dayjs.utc только как способ
//   разобрать настенные часы без сдвига в зону браузера (не как UTC-момент);
// - новые timestamp RD-165 (created_at откликов и истории, archived_at, calculated_at, conflicting_shift.*) —
//   ISO с offset, показываются в смещении department.timezone профиля (RD-160, D-001), иначе — устройства.

export function profileOffset(): number | null {
    return parseProfileOffset(useAuthStore().userInfo?.department?.timezone)
}

/** Рабочее время для API: naive локальное время подразделения, без Z и offset. */
export function buildTimestamp(date: string, time: string): string {
    return `${date}T${time}:00`
}

/** Рабочее время API (naive) как Dayjs настенного времени. */
export function workTime(iso: string): Dayjs {
    return dayjs.utc(iso)
}

/** Настенное время в мс — сравнивается только с wallNow(). */
export function instantOf(date: string, time: string): number {
    return dayjs.utc(`${date}T${time}:00`).valueOf()
}

export function workMs(iso: string): number {
    return dayjs.utc(iso).valueOf()
}

/** «Сейчас» как настенное время магазина (смещение профиля, иначе устройство). */
export function wallNow(offset: number | null = profileOffset()): number {
    if (offset === null) return dayjs.utc(dayjs().format('YYYY-MM-DDTHH:mm:ss')).valueOf()
    return Date.now() + offset * 60_000
}

/** Timestamp API с offset (created_at и т. п.) в календарном времени подразделения. */
export function toLocal(iso: string, offset: number | null = profileOffset()): Dayjs {
    return offset === null ? dayjs(iso) : dayjs(iso).utcOffset(offset)
}

export function todayKey(offset: number | null = profileOffset()): string {
    return calendarDayKey(Date.now(), offset)
}

export function addDays(dayKey: string, days: number): string {
    return dayjs(dayKey).add(days, 'day').format('YYYY-MM-DD')
}

const WEEKDAYS = ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб']
const MONTHS_GEN = ['января', 'февраля', 'марта', 'апреля', 'мая', 'июня', 'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря']
const MONTHS_NOM = ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь']

/** «Сегодня, 21.10» / «Пт, 23.10». */
export function rowDateLabel(dayKey: string, today = todayKey()): string {
    const day = dayjs(dayKey)
    const short = day.format('DD.MM')
    if (dayKey === today) return `Сегодня, ${short}`
    return `${WEEKDAYS[day.day()]}, ${short}`
}

/** «Ср, 21 октября». */
export function dayHeaderLabel(dayKey: string): string {
    const day = dayjs(dayKey)
    return `${WEEKDAYS[day.day()]}, ${day.date()} ${MONTHS_GEN[day.month()]}`
}

/** «21 октября». */
export function dayMonthLabel(dayKey: string): string {
    const day = dayjs(dayKey)
    return `${day.date()} ${MONTHS_GEN[day.month()]}`
}

export function monthLabel(dayKey: string): string {
    const day = dayjs(dayKey)
    return `${MONTHS_NOM[day.month()]} ${day.year()}`
}

/** «19.10 – 25.10.2026». */
export function periodLabel([from, to]: [string, string]): string {
    const a = dayjs(from)
    const b = dayjs(to)
    if (from === to) return b.format('DD.MM.YYYY')
    return `${a.format(a.year() === b.year() ? 'DD.MM' : 'DD.MM.YYYY')} – ${b.format('DD.MM.YYYY')}`
}

export function isFullMonth([from, to]: [string, string]): boolean {
    const a = dayjs(from)
    return a.date() === 1 && dayjs(to).isSame(a.endOf('month'), 'day')
}

export function timeRange(vacancy: Pick<Vacancy, 'start' | 'end'>): string {
    return `${workTime(vacancy.start).format('HH:mm')} – ${workTime(vacancy.end).format('HH:mm')}`
}

/** День рабочего времени. */
export function dayKeyOf(iso: string): string {
    return workTime(iso).format('YYYY-MM-DD')
}

/** День служебного момента в зоне подразделения. */
export function auditDayKey(iso: string, offset = profileOffset()): string {
    return toLocal(iso, offset).format('YYYY-MM-DD')
}

export function plural(count: number, forms: [string, string, string]): string {
    const n = Math.abs(count) % 100
    const n1 = n % 10
    if (n > 10 && n < 20) return forms[2]
    if (n1 > 1 && n1 < 5) return forms[1]
    if (n1 === 1) return forms[0]
    return forms[2]
}

export function countLabel(count: number, forms: [string, string, string]): string {
    return `${count.toLocaleString('ru-RU')} ${plural(count, forms)}`
}

export const RESPONSE_FORMS: [string, string, string] = ['отклик', 'отклика', 'откликов']
export const VACANCY_FORMS: [string, string, string] = ['вакансия', 'вакансии', 'вакансий']

type Person = Pick<UserVacancy | UserShort, 'first_name' | 'last_name'>

export function fullName(user: Person | null | undefined): string {
    if (!user) return ''
    return [user.last_name, user.first_name].filter(Boolean).join(' ')
}

/** «Соков Г.» */
export function shortName(user: Person | null | undefined): string {
    if (!user) return ''
    const initial = user.first_name ? ` ${user.first_name.charAt(0)}.` : ''
    return `${user.last_name ?? ''}${initial}`.trim()
}

export function initials(user: Person | null | undefined): string {
    if (!user) return ''
    return `${user.last_name?.charAt(0) ?? ''}${user.first_name?.charAt(0) ?? ''}`.toUpperCase()
}

// Цвет аватара по ID — стабильный, из палитры макета.
const AVATAR_TONES = ['violet', 'teal', 'amber'] as const
export type AvatarTone = typeof AVATAR_TONES[number]
export function avatarTone(id: string | null | undefined): AvatarTone {
    if (!id) return 'violet'
    let hash = 0
    for (let i = 0; i < id.length; i++) hash = (hash * 31 + id.charCodeAt(i)) | 0
    return AVATAR_TONES[Math.abs(hash) % AVATAR_TONES.length]
}

export function exchangeTypeOf(inner: boolean, outer: boolean): ExchangeType {
    if (inner && outer) return 'both'
    return outer ? 'external' : 'internal'
}

export function exchangeFlags(type: ExchangeType): { inner_exchange: boolean, outer_exchange: boolean } {
    return { inner_exchange: type !== 'external', outer_exchange: type !== 'internal' }
}

export const EXCHANGE_TYPE_LABELS: Record<ExchangeType, string> = {
    internal: 'Внутренняя',
    external: 'Внешняя',
    both: 'Обе',
}

export const SOURCE_LABELS: Record<ResponseSource, string> = {
    own_department: 'Свой сотрудник',
    other_department: 'Другой отдел',
    external_exchange: 'Внешняя биржа',
}

export const SOURCE_TONES: Record<ResponseSource, string> = {
    own_department: 'muted',
    other_department: 'waiting',
    external_exchange: 'amber',
}

export type ChipTone = 'open' | 'waiting' | 'closed' | 'rejected' | 'muted'

export interface StateChip {
    label: string
    tone: ChipTone
}

const LIFECYCLE_CHIPS: Record<Lifecycle, StateChip> = {
    needs_decision: { label: 'Ожидание', tone: 'waiting' },
    waiting_responses: { label: 'Открыта', tone: 'open' },
    closed: { label: 'Закрыта', tone: 'closed' },
    unfilled: { label: 'Не закрыта', tone: 'rejected' },
    rejected: { label: 'Отклонён', tone: 'rejected' },
    deleted: { label: 'Удалена', tone: 'muted' },
    shift_cancelled: { label: 'Смена отменена', tone: 'muted' },
}

// Подписи текущего StatusEnum (как прежний VacancyStatus) — только когда backend не отдаёт lifecycle.
const LEGACY_CHIPS: Record<LegacyStatus, StateChip> = {
    open: { label: 'Открыта', tone: 'open' },
    responded: { label: 'Ожидание', tone: 'waiting' },
    completed: { label: 'Закрыта', tone: 'closed' },
    requested: { label: 'Ожидание', tone: 'waiting' },
}

export function vacancyChip(vacancy: Vacancy): StateChip | null {
    if (vacancy.lifecycle) return LIFECYCLE_CHIPS[vacancy.lifecycle]
    if (vacancy.status) return LEGACY_CHIPS[vacancy.status]
    return null
}

export function requestChip(vacancy: Vacancy): StateChip {
    if (vacancy.lifecycle === 'rejected' || vacancy.isRequestRejected) return { label: 'Отклонён', tone: 'rejected' }
    if (vacancy.lifecycle === 'closed' || (!vacancy.lifecycle && vacancy.status === 'completed')) return { label: 'Закрыт', tone: 'closed' }
    if (vacancy.lifecycle && vacancy.lifecycle !== 'needs_decision' && vacancy.lifecycle !== 'waiting_responses') return LIFECYCLE_CHIPS[vacancy.lifecycle]
    return { label: 'Ожидание', tone: 'waiting' }
}

export const ARCHIVE_RESULT_CHIPS: Record<ArchiveResult, StateChip> = {
    closed: { label: 'Закрыта', tone: 'closed' },
    unfilled: { label: 'Не закрыта', tone: 'rejected' },
    rejected: { label: 'Отклонён', tone: 'rejected' },
    deleted: { label: 'Удалена', tone: 'muted' },
    shift_cancelled: { label: 'Смена отменена', tone: 'muted' },
}

export const ARCHIVE_RESULT_LABELS: Record<ArchiveResult, string> = {
    closed: 'Закрыта',
    unfilled: 'Не закрыта',
    rejected: 'Отклонён запрос',
    deleted: 'Удалена',
    shift_cancelled: 'Смена отменена',
}

export const REQUEST_LIFECYCLE_LABELS: Record<RequestLifecycle, string> = {
    waiting_responses: 'Ожидание',
    closed: 'Закрыт',
    rejected: 'Отклонён',
}

export function archiveChip(vacancy: Vacancy): StateChip | null {
    if (vacancy.archiveResult) return ARCHIVE_RESULT_CHIPS[vacancy.archiveResult]
    // Без archive_result (текущий backend): запись архива уже в прошлом, поэтому «Открыта/Ожидание»
    // не показываются — назначенная/completed → «Закрыта», отклонённый запрос, удалённая, иначе «Не закрыта».
    if (vacancy.raw.deleted_at) return ARCHIVE_RESULT_CHIPS.deleted
    if (vacancy.isRequestRejected) return ARCHIVE_RESULT_CHIPS.rejected
    if (vacancy.assigned || vacancy.status === 'completed') return ARCHIVE_RESULT_CHIPS.closed
    return ARCHIVE_RESULT_CHIPS.unfilled
}

/** Горящая: до начала меньше 24 ч и потребность не закрыта. */
export function isUrgent(vacancy: Vacancy, now = wallNow()): boolean {
    const diff = workMs(vacancy.start) - now
    if (diff <= 0 || diff >= 24 * 3600_000) return false
    if (vacancy.lifecycle) return vacancy.lifecycle === 'needs_decision' || vacancy.lifecycle === 'waiting_responses'
    return vacancy.status !== 'completed' && !vacancy.isRequestRejected
}

/** Нужно решение: ожидающие отклики без назначенного (target lifecycle, иначе текущие поля). */
export function needsDecision(vacancy: Vacancy): boolean {
    if (vacancy.lifecycle) return vacancy.lifecycle === 'needs_decision'
    // Текущий backend оставляет responded_users и после назначения — закрытая решения не ждёт.
    return vacancy.responses.length > 0 && !vacancy.assigned && vacancy.status !== 'completed' && !vacancy.isRequestRejected
}

export function hoursUntil(vacancy: Vacancy, now = wallNow()): number {
    return Math.max(0, Math.round((workMs(vacancy.start) - now) / 3600_000))
}
